import { useState } from "react";
import { useLoaderData, useRevalidator, useRouteLoaderData } from "react-router";
import { Table } from "@/components/application/table/table";
import { ConfirmButton } from "@/components/admin/confirm-button";
import { CopyField } from "@/components/admin/copy-field";
import { Deferred, FieldSkeleton, Loading, Skeleton, TableSkeleton } from "@/components/admin/loading";
import { useToast } from "@/components/admin/toast";
import { Input } from "@/components/base/input/input";
import { formatDate, isAdmin, type AdminMe, type Api, type PlatformUser, type UserSession } from "../lib/api.js";
import { Card, PageHeader } from "./shell.jsx";
import { UserBadges } from "./users.jsx";

// Ending a session deletes it on the server, but a browser that still holds the session cookie's
// cached copy keeps being served until that copy expires. Said wherever a session is ended.
const CACHE_NOTE = "对方已打开的页面最长 5 分钟内失效。";

/** Who (a name, an email, badges, two buttons), their id and sign-up, then their sessions. */
export function UserSkeleton() {
  return (
    <Loading>
      <PageHeader
        title={<Skeleton className="my-1 h-6 w-48" />}
        description={<Skeleton className="my-0.5 w-56" />}
        badge={<Skeleton className="h-6 w-24 rounded-full" />}
        actions={
          <>
            <Skeleton className="h-10 w-28 rounded-lg" />
            <Skeleton className="h-10 w-20 rounded-lg" />
          </>
        }
      />
      <Card>
        <div className="grid gap-5 p-6 sm:grid-cols-2">
          <FieldSkeleton label="用户 ID" />
          <div className="flex flex-col gap-1.5">
            <span className="text-sm font-medium text-secondary">注册时间</span>
            <Skeleton className="my-2 w-36" />
          </div>
        </div>
      </Card>
      <Card title={<Skeleton className="my-0.5 h-5 w-20" />}>
        <TableSkeleton columns={["设备", "IP", "开始", "到期", ""]} rows={2} size="sm" bordered={false} />
      </Card>
    </Loading>
  );
}

/** One person: who they are, whether they may use this panel, whether they may sign in at all, and what they have open. */
export function UserRoute() {
  const { api, data } = useLoaderData<{ api: Api; data: Promise<[PlatformUser, UserSession[]]> }>();
  return (
    <Deferred data={data} fallback={<UserSkeleton />}>
      {([user, sessions]) => <UserPage api={api} user={user} sessions={sessions} />}
    </Deferred>
  );
}

function UserPage({ api, user, sessions }: { api: Api; user: PlatformUser; sessions: UserSession[] }) {
  const me = (useRouteLoaderData("shell") as { me: AdminMe }).me;
  const toast = useToast();
  const revalidator = useRevalidator();
  const [reason, setReason] = useState("");
  const self = user.id === me.id;

  const act = async (work: () => Promise<unknown>, done: string) => {
    try {
      await work();
      toast.success(done);
      await revalidator.revalidate();
    } catch (err) {
      toast.error(err);
    }
  };

  return (
    <>
      <PageHeader
        title={user.name || user.email}
        description={user.email}
        badge={<UserBadges user={user} />}
        actions={
          <>
            {isAdmin(user) ? (
              // Nobody takes the panel away from themselves by accident: someone else has to.
              <ConfirmButton color="secondary" size="md" isDisabled={self} title={`撤销 ${user.email} 的管理员身份？`} confirmLabel="撤销" destructive onConfirm={() => act(() => api.setRole(user.id, "user"), "已撤销管理员身份")}>
                撤销管理员
              </ConfirmButton>
            ) : (
              <ConfirmButton color="secondary" size="md" title={`将 ${user.email} 设为平台管理员？`} description="管理员可以查看所有用户与工作区，封禁用户并结束他们的会话。" confirmLabel="设为管理员" onConfirm={() => act(() => api.setRole(user.id, "admin"), "已设为管理员")}>
                设为管理员
              </ConfirmButton>
            )}
            {user.banned ? (
              <ConfirmButton color="secondary" size="md" title={`解除对 ${user.email} 的封禁？`} confirmLabel="解除封禁" onConfirm={() => act(() => api.unbanUser(user.id), "已解除封禁")}>
                解除封禁
              </ConfirmButton>
            ) : (
              <ConfirmButton
                color="secondary-destructive"
                size="md"
                isDisabled={self}
                title={`封禁 ${user.email}？`}
                description={`对方将无法登录，现有会话全部结束。${CACHE_NOTE}`}
                body={<Input label="原因（可选）" value={reason} onChange={setReason} />}
                confirmLabel="封禁"
                destructive
                onConfirm={() => act(() => api.banUser(user.id, reason.trim()), "已封禁")}
              >
                封禁
              </ConfirmButton>
            )}
          </>
        }
      />
      <Card>
        <div className="grid gap-5 p-6 sm:grid-cols-2">
          <CopyField label="用户 ID" value={user.id} />
          <div className="flex flex-col gap-1.5">
            <span className="text-sm font-medium text-secondary">注册时间</span>
            <span className="py-1.5 text-sm text-primary">{formatDate(user.createdAt)}</span>
          </div>
          {user.banned && (
            <div className="flex flex-col gap-1.5 sm:col-span-2">
              <span className="text-sm font-medium text-secondary">封禁原因</span>
              <span className="text-sm text-primary">{user.banReason || "未填写"}</span>
            </div>
          )}
        </div>
      </Card>
      <Card
        title={`会话（${sessions.length}）`}
        actions={
          sessions.length > 0 && (
            <ConfirmButton color="secondary-destructive" size="sm" title={`结束 ${user.email} 的全部会话？`} description={`对方需要重新登录。${CACHE_NOTE}`} confirmLabel="全部结束" destructive onConfirm={() => act(() => api.revokeSessions(user.id), "已结束全部会话")}>
              全部结束
            </ConfirmButton>
          )
        }
      >
        {sessions.length === 0 ? (
          <p className="px-6 py-8 text-sm text-tertiary">没有进行中的会话。</p>
        ) : (
          <Table aria-label="会话" size="sm">
            <Table.Header bordered={false}>
              <Table.Head id="agent" label="设备" isRowHeader />
              <Table.Head id="ip" label="IP" />
              <Table.Head id="createdAt" label="开始" />
              <Table.Head id="expiresAt" label="到期" />
              <Table.Head id="action" />
            </Table.Header>
            <Table.Body items={sessions}>
              {(s) => (
                <Table.Row id={s.id}>
                  <Table.Cell className="max-w-64 truncate">{s.userAgent || "未知"}</Table.Cell>
                  <Table.Cell>{s.ipAddress || "未知"}</Table.Cell>
                  <Table.Cell>{formatDate(s.createdAt)}</Table.Cell>
                  <Table.Cell>{formatDate(s.expiresAt)}</Table.Cell>
                  <Table.Cell className="text-right">
                    <ConfirmButton color="link-destructive" size="sm" title="结束这个会话？" description={CACHE_NOTE} confirmLabel="结束" destructive onConfirm={() => act(() => api.revokeSession(s.token), "已结束会话")}>
                      结束
                    </ConfirmButton>
                  </Table.Cell>
                </Table.Row>
              )}
            </Table.Body>
          </Table>
        )}
      </Card>
    </>
  );
}
